import { baseApi } from 'shared/api/base-api';
import type { ResolverTeam, StandingsResolverContext } from 'modules/manager/domain/placeholder';

export interface StandingTeamDto {
  id: number;
  name: string;
  shortName: string;
  emblem?: { externalUrl?: string };
}

export interface StandingRowDto {
  position: number;
  team: StandingTeamDto;
  gamesPlayed: number;
  gamesWon: number;
  gamesDraw: number;
  gamesLost: number;
  goalsWon: number;
  goalsLost: number;
  championshipScore: number;
}

export interface TeamGroupDto {
  id: number;
  name: string;
  groupChampionshipTable?: { rows: StandingRowDto[] } | null;
}

export interface StageStandingsDto {
  id: number;
  name: string;
  type: string;
  championship?: {
    id: number;
    name: string;
    images?: { externalUrl?: string }[];
    tournament?: { id: number; name: string; images?: { externalUrl?: string }[] };
  } | null;
  stageChampionshipTable?: { rows: StandingRowDto[] } | null;
  teamGroups?: TeamGroupDto[] | null;
}

interface StageStandingsRenderResponse {
  stage: StageStandingsDto | null;
}

export interface StandingsRenderArgs {
  stageId: number;
}

export interface StandingsRenderContext {
  stage: { id: number; name: string; type: string };
  /**
   * One entry per group for a GROUP-type stage (each carries `group`), or a
   * single entry from the flat stage table for every other stage type.
   */
  targets: readonly StandingsResolverContext[];
}

// Why one `stage(id)` query instead of a per-group request: the backend
// already returns the whole group->table tree (or the flat
// stageChampionshipTable for non-GROUP stages) from one field set, so this
// endpoint issues exactly one GraphQL request regardless of how many groups
// the stage has — no per-match, no per-group fan-out.
const STAGE_STANDINGS_RENDER_QUERY = `#graphql
query getStageStandingsRender($stageId: Int!) {
  stage(id: $stageId) {
    id
    name
    type
    championship {
      id
      name
      images { externalUrl }
      tournament { id name images { externalUrl } }
    }
    stageChampionshipTable {
      rows {
        position
        team { id name shortName emblem { externalUrl } }
        gamesPlayed
        gamesWon
        gamesDraw
        gamesLost
        goalsWon
        goalsLost
        championshipScore
      }
    }
    teamGroups {
      id
      name
      groupChampionshipTable {
        rows {
          position
          team { id name shortName emblem { externalUrl } }
          gamesPlayed
          gamesWon
          gamesDraw
          gamesLost
          goalsWon
          goalsLost
          championshipScore
        }
      }
    }
  }
}
`;

// The manager module has no dependency on the tournaments module's
// StageType enum (cross-module domain imports are avoided by convention);
// the schema's StageType.GROUP value is the literal string 'GROUP' — see
// docs/schema.graphql.
const GROUP_STAGE_TYPE = 'GROUP';

export const standingsRenderApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getStandingsRenderContext: build.query<StandingsRenderContext, StandingsRenderArgs>({
      async queryFn({ stageId }, _api, _extra, baseQuery) {
        const res = await baseQuery({ query: STAGE_STANDINGS_RENDER_QUERY, variables: { stageId } });
        if (res.error) return { error: res.error };
        const stage = (res.data as StageStandingsRenderResponse | undefined)?.stage;
        if (!stage) return { error: { status: 'CUSTOM_ERROR', error: 'Stage not found' } as never };

        return { data: buildStandingsRenderContext(stage) };
      },
      keepUnusedDataFor: 30,
    }),
  }),
  overrideExisting: false,
});

/**
 * Pure transform from the `stage(id)` GraphQL response into the render
 * context: shared tournament/championship head, one target per team group
 * for a GROUP stage, or a single target from the flat stage table for every
 * other stage type. Exported (not just used internally) so the GROUP vs
 * non-GROUP shaping and the row→ResolverTeam field mapping can be unit
 * tested without going through the network/queryFn.
 */
export function buildStandingsRenderContext(stage: StageStandingsDto): StandingsRenderContext {
  const tournament = {
    id: stage.championship?.tournament?.id ?? 0,
    name: stage.championship?.tournament?.name ?? '',
    logoUrl: stage.championship?.tournament?.images?.[0]?.externalUrl,
  };
  const championship = {
    id: stage.championship?.id ?? 0,
    name: stage.championship?.name ?? '',
    logoUrl: stage.championship?.images?.[0]?.externalUrl,
  };
  const stageHead = { id: stage.id, name: stage.name, type: stage.type };

  const targets: StandingsResolverContext[] =
    stage.type === GROUP_STAGE_TYPE
      ? (stage.teamGroups ?? []).map((group) => ({
          mode: 'standings' as const,
          tournament,
          championship,
          stage: stageHead,
          group: { id: group.id, name: group.name },
          standingTeams: toStandingTeams(group.groupChampionshipTable?.rows),
        }))
      : [
          {
            mode: 'standings' as const,
            tournament,
            championship,
            stage: stageHead,
            standingTeams: toStandingTeams(stage.stageChampionshipTable?.rows),
          },
        ];

  return { stage: stageHead, targets };
}

export function toStandingTeams(rows: StandingRowDto[] | undefined): ResolverTeam[] {
  return [...(rows ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((row) => ({
      id: row.team.id,
      name: row.team.name,
      shortName: row.team.shortName,
      logoUrl: row.team.emblem?.externalUrl,
      standing: {
        position: row.position,
        gamesPlayed: row.gamesPlayed,
        gamesWon: row.gamesWon,
        gamesLost: row.gamesLost,
        gamesDrawn: row.gamesDraw,
        goalsScored: row.goalsWon,
        goalsConceded: row.goalsLost,
        points: row.championshipScore,
      },
    }));
}

export const { useGetStandingsRenderContextQuery } = standingsRenderApi;
